import React, { useState } from "react"
import Layout from "../components/common/Layout"
import { ClientOnly } from "../components/common"
import { MetaData as MetaTags } from "../components/common/meta"
import FirebaseProvider from "../components/idealline/Firebase"
import IRacingImport from "../components/idealline/IRacingImport"
import Notification from "../components/common/Notification"
import TelemetryLandingPage from "../components/landing/TelemetryLandingPage"
import { onImportLine } from "../utils/lineUtils"
import { trackEvent } from "../utils/analytics"

const IRacingPage = ({ location }) => {
    const [importError, setImportError] = useState(null)
    const [importWarning, setImportWarning] = useState(null)
    const [triggerOpen, setTriggerOpen] = useState(false)

    const openImportModal = () => {
        setTriggerOpen(true)
        setTimeout(() => setTriggerOpen(false), 100)
    }

    const handleImport = async (points, carInfo, telemetryId) => {
        setImportError(null)
        setImportWarning(null)

        if (!points) {
            setImportError(
                carInfo?.message ||
                    "Failed to extract data from the telemetry file."
            )
            return
        }

        try {
            const result = await onImportLine({ points, carInfo, telemetryId })
            if (!result.lineId) {
                trackEvent("telemetry_import_error", {
                    product: "iracing",
                    placement: "importer",
                    stage: "line_creation",
                    error_code: "line_creation_failed",
                })
                setImportError(
                    "Failed to create the racing line. Please try again."
                )
                return
            }
            if (result.warning) setImportWarning(result.warning)
            trackEvent("telemetry_import_complete", {
                product: "iracing",
                placement: "importer",
                stage: "line_creation",
            })
            window.location.href = `/telemetry-report/?id=${encodeURIComponent(telemetryId)}&lineUrl=${encodeURIComponent(result.url)}`
        } catch (error) {
            trackEvent("telemetry_import_error", {
                product: "iracing",
                placement: "importer",
                stage: "line_creation",
                error_code: "line_creation_failed",
            })
            setImportError(
                error.message || "The import could not be completed."
            )
        }
    }

    return (
        <Layout>
            <MetaTags
                title="iRacing Telemetry Analysis | Lap Comparison and Coaching"
                description="Analyze iRacing IBT files in 3D. Compare racing lines, braking, throttle, speed and steering, then get focused coaching for your next session."
                location={location}
            />
            <TelemetryLandingPage
                provider="iRacing"
                providerKey="iracing"
                eyebrow="Analyze iRacing IBT files"
                title="iRacing telemetry analysis"
                accentTitle="that shows where the lap went"
                lede="Compare your racing line, braking, throttle, steering and section times. Open Racer turns the measured differences into a focused coaching plan for your next session."
                sampleHref="/iracing-coaching-report/"
                sampleTitle="Porsche GT3 at Algarve"
                sampleDescription="See how the analyzer turns a measured Porsche 911 GT3 R session into section comparisons, driving observations and specific tests."
                sampleMetrics={[
                    { value: "1:46.910", label: "reference lap" },
                    { value: "7", label: "measured laps" },
                    { value: "7", label: "analyzed sections" },
                    { value: "4.58 km", label: "circuit length" },
                ]}
                sampleHighlights={[
                    "See the measured racing line in the real 3D circuit context",
                    "Compare braking, throttle, speed and steering by section",
                    "Find where one lap gained or lost time without adding unrelated corners together",
                    "Turn each observation into one practical change to test",
                ]}
                analysisExample={{
                    title: "Turn 1 double apex",
                    summary:
                        "The slower lap gave away 0.167 seconds through the section. Minimum speed fell to 135.6 km/h versus 142.7, then exit speed remained 2.7 km/h lower.",
                    metrics: [
                        { value: "+0.167 s", label: "section difference" },
                        { value: "7.1 km/h", label: "minimum speed gap" },
                        { value: "2.7 km/h", label: "exit speed gap" },
                    ],
                    action: "Hold the braking marker constant for three laps. Change only turn in timing and keep the lap with the smallest steering correction after the first apex.",
                }}
                importTitle="Import your iRacing telemetry"
                importDescription="Choose an IBT file from your iRacing telemetry folder. Open Racer reads the recorded laps and builds the racing line from the session data."
                importButtonLabel="Import an iRacing session"
                onOpenImport={openImportModal}
                importExperience={
                    <ClientOnly>
                        <FirebaseProvider>
                            <IRacingImport
                                triggerOpen={triggerOpen}
                                onImport={handleImport}
                            />
                        </FirebaseProvider>
                    </ClientOnly>
                }
                requirements={[
                    "A native iRacing IBT telemetry file",
                    "At least one completed lap",
                    "GPS position channels recorded in the session",
                    "The file from Documents/iRacing/telemetry",
                ]}
                steps={[
                    {
                        title: "Record the session",
                        description:
                            "Enable iRacing telemetry and complete your laps as normal. The simulator writes the IBT file locally.",
                    },
                    {
                        title: "Choose the IBT file",
                        description:
                            "Open the importer and select the session you want to study. Open Racer validates the recorded channels before creating anything.",
                    },
                    {
                        title: "Review the lap",
                        description:
                            "Use the 3D line and measured traces to study individual sections and define the next useful test.",
                    },
                ]}
                features={[
                    {
                        title: "Real circuit context",
                        description:
                            "The measured line sits on Google Maps 3D so the track shape, elevation and surroundings remain visible.",
                    },
                    {
                        title: "Inputs beside outcomes",
                        description:
                            "Speed, brake, throttle and steering are aligned by lap distance instead of shown as disconnected charts.",
                    },
                    {
                        title: "Actionable sections",
                        description:
                            "The report turns differences into bounded coaching instructions rather than promising theoretical gains.",
                    },
                ]}
                faqs={[
                    {
                        question: "Where does iRacing save IBT telemetry?",
                        answer: "iRacing saves telemetry in the Documents/iRacing/telemetry folder after telemetry recording has been enabled for the session.",
                    },
                    {
                        question: "How does iRacing telemetry coaching work?",
                        answer: "Open Racer compares distance-aligned laps from your IBT file, then connects time differences to the racing line, braking, throttle, speed and steering recorded in each section. The report turns those measurements into a small number of changes to test.",
                    },
                    {
                        question:
                            "Can I view the sample without a subscription?",
                        answer: "Yes. The complete Porsche GT3 Algarve sample is public and can be explored without an account or Paddock Pass.",
                    },
                    {
                        question: "What requires Paddock Pass?",
                        answer: "Creating a new racing line from your own IBT file requires Paddock Pass from the first import. Viewing the public sample remains free.",
                    },
                    {
                        question: "Does the report invent a theoretical lap?",
                        answer: "No. The sample keeps measured lap evidence separate from coaching interpretation and does not add independent section gains into a promised lap time.",
                    },
                ]}
            />
            <div className="fixed left-1/2 top-32 z-[9999] w-full max-w-sm -translate-x-1/2 space-y-2">
                {importError && (
                    <Notification
                        message={importError}
                        type="error"
                        onClose={() => setImportError(null)}
                    />
                )}
                {importWarning && (
                    <Notification
                        message={importWarning}
                        type="warning"
                        onClose={() => setImportWarning(null)}
                    />
                )}
            </div>
        </Layout>
    )
}

export default IRacingPage
